// Vault encryption service using Web Crypto API
// Uses PBKDF2 for key derivation and AES-GCM for encryption

import { EncryptedBlob } from '../models/vault.model';

const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const KEY_LENGTH = 256;

// Convert ArrayBuffer to Base64
function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Convert Base64 to ArrayBuffer
function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Generate cryptographically secure random bytes
function generateRandomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

// Derive encryption key from passphrase using PBKDF2
export async function deriveKey(
  passphrase: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passphraseKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passphraseKey,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

// Encrypt plaintext with passphrase
export async function encrypt(
  plaintext: string,
  passphrase: string
): Promise<EncryptedBlob> {
  const encoder = new TextEncoder();
  const salt = generateRandomBytes(SALT_LENGTH);
  const iv = generateRandomBytes(IV_LENGTH);
  const key = await deriveKey(passphrase, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  );

  return {
    iv: bufferToBase64(iv.buffer),
    ciphertext: bufferToBase64(ciphertext),
    salt: bufferToBase64(salt.buffer),
  };
}

// Decrypt ciphertext with passphrase
export async function decrypt(
  blob: EncryptedBlob,
  passphrase: string
): Promise<string> {
  const decoder = new TextDecoder();
  const salt = new Uint8Array(base64ToBuffer(blob.salt));
  const iv = new Uint8Array(base64ToBuffer(blob.iv));
  const ciphertext = base64ToBuffer(blob.ciphertext);
  const key = await deriveKey(passphrase, salt);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  return decoder.decode(plaintext);
}

// Encrypt with a pre-derived key (for session-based encryption)
export async function encryptWithKey(
  plaintext: string,
  key: CryptoKey,
  salt: Uint8Array
): Promise<EncryptedBlob> {
  const encoder = new TextEncoder();
  const iv = generateRandomBytes(IV_LENGTH);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  );

  return {
    iv: bufferToBase64(iv.buffer),
    ciphertext: bufferToBase64(ciphertext),
    salt: bufferToBase64(salt.buffer),
  };
}

// Decrypt with a pre-derived key (for session-based decryption)
export async function decryptWithKey(
  blob: EncryptedBlob,
  key: CryptoKey
): Promise<string> {
  const decoder = new TextDecoder();
  const iv = new Uint8Array(base64ToBuffer(blob.iv));
  const ciphertext = base64ToBuffer(blob.ciphertext);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  return decoder.decode(plaintext);
}

// Vault session management
let sessionKey: CryptoKey | null = null;
let sessionSalt: Uint8Array | null = null;
let sessionExpiresAt: number | null = null;

const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

export async function unlockVault(passphrase: string): Promise<boolean> {
  try {
    const salt = generateRandomBytes(SALT_LENGTH);
    const key = await deriveKey(passphrase, salt);

    // Test encryption/decryption works
    const testData = 'vault-unlock-test';
    const encrypted = await encryptWithKey(testData, key, salt);
    const decrypted = await decryptWithKey(encrypted, key);

    if (decrypted !== testData) {
      return false;
    }

    sessionKey = key;
    sessionSalt = salt;
    sessionExpiresAt = Date.now() + SESSION_DURATION_MS;
    return true;
  } catch {
    return false;
  }
}

export function lockVault(): void {
  sessionKey = null;
  sessionSalt = null;
  sessionExpiresAt = null;
}

export function isVaultUnlocked(): boolean {
  if (!sessionKey || !sessionExpiresAt) return false;
  if (Date.now() > sessionExpiresAt) {
    lockVault();
    return false;
  }
  return true;
}

export function extendSession(): void {
  if (sessionKey && sessionExpiresAt) {
    sessionExpiresAt = Date.now() + SESSION_DURATION_MS;
  }
}

export async function encryptPassword(password: string): Promise<EncryptedBlob> {
  if (!sessionKey || !sessionSalt) {
    throw new Error('Vault is locked. Please unlock first.');
  }
  extendSession();
  return encryptWithKey(password, sessionKey, sessionSalt);
}

export async function decryptPassword(blob: EncryptedBlob): Promise<string> {
  if (!sessionKey) {
    throw new Error('Vault is locked. Please unlock first.');
  }
  extendSession();

  // Need to re-derive key with the stored salt
  // This is a simplified approach - in production you'd store the salt per-item
  // For now, we'll use the passphrase-based decryption
  return decryptWithKey(blob, sessionKey);
}

// Store passphrase temporarily for re-deriving keys
let sessionPassphrase: string | null = null;

export async function unlockVaultWithPassphrase(passphrase: string): Promise<boolean> {
  const success = await unlockVault(passphrase);
  if (success) {
    sessionPassphrase = passphrase;
  }
  return success;
}

export async function encryptWithPassphrase(plaintext: string): Promise<EncryptedBlob> {
  if (!sessionPassphrase) {
    throw new Error('Vault is locked. Please unlock first.');
  }
  extendSession();
  return encrypt(plaintext, sessionPassphrase);
}

export async function decryptWithPassphrase(blob: EncryptedBlob): Promise<string> {
  if (!sessionPassphrase) {
    throw new Error('Vault is locked. Please unlock first.');
  }
  extendSession();
  return decrypt(blob, sessionPassphrase);
}

export function lockVaultFull(): void {
  lockVault();
  sessionPassphrase = null;
}

export function getSessionTimeRemaining(): number {
  if (!sessionExpiresAt) return 0;
  return Math.max(0, sessionExpiresAt - Date.now());
}
